import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './firebase';
import { toast } from 'react-toastify';

// Configuration par défaut pour l'optimisation des images
const IMAGE_CONFIG = {
  MAX_WIDTH: 1200,
  MAX_HEIGHT: 1200,
  JPEG_QUALITY: 0.8,
  WEBP_QUALITY: 0.8,
  MAX_FILE_SIZE: 2 * 1024 * 1024, // 2 MB
  TARGET_SIZE: 300 * 1024, // 300 KB
  MAX_COMPRESSED_SIZE: 500 * 1024, // 500 KB
};

interface OptimizationResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

class ImageOptimizationService {
  /**
   * Valide le fichier image avant traitement
   */
  private validateImageFile(file: File): void {
    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      throw new Error('Le fichier doit être une image');
    }

    // Vérifier la taille du fichier
    if (file.size > IMAGE_CONFIG.MAX_FILE_SIZE) {
      throw new Error(`L'image ne doit pas dépasser ${IMAGE_CONFIG.MAX_FILE_SIZE / (1024 * 1024)} MB`);
    }
  }

  /**
   * Redimensionne une image en conservant les proportions
   */
  private resizeImage(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, img: HTMLImageElement): void {
    let { width, height } = img;
    
    // Calculer les nouvelles dimensions en conservant le ratio
    if (width > height) {
      if (width > IMAGE_CONFIG.MAX_WIDTH) {
        height = (height * IMAGE_CONFIG.MAX_WIDTH) / width;
        width = IMAGE_CONFIG.MAX_WIDTH;
      }
    } else {
      if (height > IMAGE_CONFIG.MAX_HEIGHT) {
        width = (width * IMAGE_CONFIG.MAX_HEIGHT) / height;
        height = IMAGE_CONFIG.MAX_HEIGHT;
      }
    }

    // Redimensionner le canvas
    canvas.width = width;
    canvas.height = height;

    // Dessiner l'image redimensionnée
    ctx.drawImage(img, 0, 0, width, height);
  }

  /**
   * Compresse une image en ajustant la qualité
   */
  private async compressImage(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob> {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob!);
      }, mimeType, quality);
    });
  }

  /**
   * Optimise une image (redimensionnement + compression)
   */
  async optimizeImage(file: File): Promise<OptimizationResult> {
    this.validateImageFile(file);

    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Impossible de créer le contexte canvas'));
        return;
      }

      img.onload = async () => {
        try {
          // Redimensionner l'image
          this.resizeImage(canvas, ctx, img);

          // Déterminer le format optimal
          const supportsWebP = await this.supportsWebP();
          const mimeType = supportsWebP ? 'image/webp' : 'image/jpeg';
          const quality = supportsWebP ? IMAGE_CONFIG.WEBP_QUALITY : IMAGE_CONFIG.JPEG_QUALITY;

          // Compression initiale
          let compressedBlob = await this.compressImage(canvas, mimeType, quality);
          let currentQuality = quality;

          // Réduire la qualité si l'image est encore trop lourde
          while (compressedBlob.size > IMAGE_CONFIG.MAX_COMPRESSED_SIZE && currentQuality > 0.3) {
            currentQuality -= 0.1;
            compressedBlob = await this.compressImage(canvas, mimeType, currentQuality);
          }

          // Si encore trop lourd, réduire davantage la résolution
          if (compressedBlob.size > IMAGE_CONFIG.MAX_COMPRESSED_SIZE) {
            const reductionFactor = 0.8;
            canvas.width *= reductionFactor;
            canvas.height *= reductionFactor;
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            compressedBlob = await this.compressImage(canvas, mimeType, currentQuality);
          }

          // Créer le fichier optimisé
          const extension = mimeType === 'image/webp' ? '.webp' : '.jpg';
          const fileName = file.name.replace(/\.[^/.]+$/, '') + extension;
          const optimizedFile = new File([compressedBlob], fileName, { type: mimeType });

          const result: OptimizationResult = {
            file: optimizedFile,
            originalSize: file.size,
            compressedSize: optimizedFile.size,
            compressionRatio: Math.round((1 - optimizedFile.size / file.size) * 100)
          };

          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Impossible de charger l\'image'));
      };

      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Vérifie si le navigateur supporte WebP
   */
  private async supportsWebP(): Promise<boolean> {
    return new Promise((resolve) => {
      const webP = new Image();
      webP.onload = webP.onerror = () => {
        resolve(webP.height === 2);
      };
      webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    });
  }

  /**
   * Upload une image optimisée vers Firebase Storage
   */
  async uploadOptimizedImage(file: File, path: string): Promise<string> {
    try {
      // Optimiser l'image
      const optimizationResult = await this.optimizeImage(file);
      
      // Afficher les statistiques d'optimisation
      // Image optimisée avec succès
      
      if (optimizationResult.compressionRatio > 0) {
        toast.success(`Image optimisée: ${optimizationResult.compressionRatio}% de réduction de taille`);
      }

      // Upload vers Firebase Storage
      const imageRef = ref(storage, path);
      const snapshot = await uploadBytes(imageRef, optimizationResult.file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error: any) {
      console.error('Erreur lors de l\'optimisation/upload:', error);
      toast.error(error.message || 'Erreur lors du téléchargement de l\'image');
      throw error;
    }
  }

  /**
   * Supprime une image de Firebase Storage
   */
  async deleteImage(imageUrl: string): Promise<void> {
    try {
      // Extraire le chemin depuis l'URL Firebase Storage
      const url = new URL(imageUrl);
      const pathMatch = url.pathname.match(/\/o\/(.*?)\?/);
      
      if (!pathMatch) {
        throw new Error('URL d\'image invalide');
      }
      
      const imagePath = decodeURIComponent(pathMatch[1]);
      const imageRef = ref(storage, imagePath);
      await deleteObject(imageRef);
      
      // Image supprimée du storage
    } catch (error: any) {
      console.warn('Erreur lors de la suppression de l\'image:', error.message);
      // Ne pas faire échouer l'opération si la suppression de l'image échoue
    }
  }

  /**
   * Nettoie les images orphelines (à implémenter selon les besoins)
   */
  async cleanupOrphanedImages(restaurantId: string, usedImageUrls: string[]): Promise<void> {
    // Cette méthode pourrait être implémentée pour nettoyer les images non utilisées
    // en listant tous les fichiers dans le dossier du restaurant et en supprimant
    // ceux qui ne sont pas dans la liste des URLs utilisées
    // Nettoyage des images orphelines à implémenter
  }

  /**
   * Calcule la consommation de stockage estimée
   */
  calculateStorageUsage(imageCount: number, averageSize: number = IMAGE_CONFIG.TARGET_SIZE): string {
    const totalBytes = imageCount * averageSize;
    const totalMB = totalBytes / (1024 * 1024);
    
    if (totalMB < 1) {
      return `${Math.round(totalBytes / 1024)} KB`;
    } else if (totalMB < 1024) {
      return `${totalMB.toFixed(1)} MB`;
    } else {
      return `${(totalMB / 1024).toFixed(2)} GB`;
    }
  }
}

export const imageOptimizationService = new ImageOptimizationService();
export { IMAGE_CONFIG };